import { useMemo, useEffect, useRef } from 'react';
import { AtSign, MessageSquare, Mail, Radio, Phone, ExternalLink, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/utils/format';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { InstagramIcon } from '@/components/icons/instagram-icon';
import { TelegramIcon } from '@/components/icons/telegram-icon';
import { useConversationStore } from '@/stores/conversation-store';
import type { MentionDocument } from '@/stores/conversation-store';
import type { Conversation, Channel } from '@/types/messaging';

const CHANNEL_ICONS: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
  INSTAGRAM: InstagramIcon,
  TELEGRAM: TelegramIcon,
};

const CHANNEL_LABELS: Record<Channel, string> = {
  SMS: 'SMS',
  MMS: 'MMS',
  WHATSAPP: 'WhatsApp',
  VOICE: 'Voice',
  EMAIL: 'Email',
  INTERCOM: 'Message',
  INSTAGRAM: 'Instagram',
  TELEGRAM: 'Telegram',
};

interface MentionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenConversation: (conv: Conversation) => void;
}

export function MentionsDialog({ open, onOpenChange, onOpenConversation }: MentionsDialogProps) {
  const mentions = useConversationStore((s) => s.mentions);
  const conversations = useConversationStore((s) => s.conversations);
  const dismissMention = useConversationStore((s) => s.dismissMention);
  const fetchMentions = useConversationStore((s) => s.fetchMentions);
  const panelRef = useRef<HTMLDivElement>(null);

  // Lazy-fetch mentions when the dialog is first opened
  useEffect(() => {
    if (open) fetchMentions();
  }, [open, fetchMentions]);

  // Sort by most recent first
  const sortedMentions = useMemo(
    () => [...mentions].sort((a, b) => b.createdTime - a.createdTime),
    [mentions]
  );

  const unreadCount = useMemo(() => mentions.filter((m) => !m.read).length, [mentions]);

  // Lookup map: conversationId -> Conversation
  const convMap = useMemo(() => {
    const map = new Map<string, Conversation>();
    for (const c of conversations) {
      map.set(c.id, c);
    }
    return map;
  }, [conversations]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    // Delay so the opening click doesn't immediately close
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onOpenChange]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="absolute top-full right-0 mt-1 z-50 w-80 rounded-xl border border-border bg-card shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-150"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
        <AtSign className="h-4 w-4 text-blue-500" />
        <span className="text-sm font-semibold text-foreground">Mentions</span>
        {unreadCount > 0 && (
          <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white px-1.5">
            {unreadCount}
          </span>
        )}
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-[360px]">
        {sortedMentions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <AtSign className="h-6 w-6 text-muted-foreground/30 mb-2" />
            <p className="text-xs font-medium text-muted-foreground">No mentions yet</p>
            <p className="text-[11px] text-muted-foreground/60 mt-0.5">
              @mentions in notes will appear here
            </p>
          </div>
        ) : (
          <div className="py-1">
            {sortedMentions.map((mention) => {
              const conv = convMap.get(mention.conversationId);
              return (
                <MentionItem
                  key={mention.id}
                  mention={mention}
                  conversation={conv || null}
                  onOpen={() => {
                    dismissMention(mention.id);
                    onOpenChange(false);
                    if (conv) onOpenConversation(conv);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Individual mention item ────────────────────────── */

function MentionItem({
  mention,
  conversation,
  onOpen,
}: {
  mention: MentionDocument;
  conversation: Conversation | null;
  onOpen: () => void;
}) {
  const ChannelIcon = conversation
    ? (CHANNEL_ICONS[conversation.channel] || Phone)
    : Phone;

  return (
    <div
      className={cn(
        'group mx-1.5 mb-0.5 rounded-lg cursor-pointer transition-colors px-3 py-2',
        mention.read
          ? 'hover:bg-muted/60'
          : 'bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-100/50 dark:hover:bg-blue-950/30'
      )}
      onClick={onOpen}
      role="button"
      tabIndex={0}
    >
      {/* Row 1: Note body */}
      <div className="flex items-start gap-2">
        {!mention.read && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
        )}
        <span className={cn(
          'text-[13px] leading-snug line-clamp-2 flex-1',
          mention.read ? 'text-muted-foreground' : 'font-medium text-foreground'
        )}>
          {mention.noteBody || 'Mentioned you in a note'}
        </span>
      </div>

      {/* Row 2: Who mentioned + when */}
      <div className={cn('flex items-center gap-1.5 mt-1', mention.read ? 'pl-0' : 'pl-4')}>
        <div
          className="h-4 w-4 rounded-full bg-primary/10 text-primary text-[8px] font-bold flex items-center justify-center shrink-0"
          title={mention.mentionedBy}
        >
          {(mention.mentionedByName || mention.mentionedBy || '?').charAt(0).toUpperCase()}
        </div>
        <span className="text-[11px] text-muted-foreground truncate">
          {mention.mentionedByName || mention.mentionedBy?.split('@')[0] || 'Someone'}
        </span>
        <span className="text-muted-foreground/30">&middot;</span>
        <span className="text-[10px] text-muted-foreground/50 shrink-0">
          {mention.createdTime ? formatTimeAgo(mention.createdTime) : ''}
        </span>
      </div>

      {/* Row 3: Conversation context */}
      {conversation && (
        <div className={cn('flex items-center gap-1 mt-1', mention.read ? 'pl-0' : 'pl-4')}>
          <ChannelIcon className="h-3 w-3 shrink-0 text-muted-foreground/40" />
          <span className="text-[10px] text-muted-foreground/50 truncate max-w-[180px]">
            {conversation.contactName || conversation.contactPhone || 'Unknown'}
          </span>
          <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-60 text-muted-foreground/40 transition-opacity" />
        </div>
      )}
    </div>
  );
}
